import type { JSX } from "preact";
import { useState } from "preact/hooks";
import { store } from "../../store/store";
import { toast } from "./Toast";
import { ConfirmDialog } from "../ui/States";
import { Avatar } from "../ui/Avatar";
import { hashFor, type Page } from "../../lib/route";

function DashboardIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
      <rect x="3" y="3" width="7" height="9" rx="1.2" />
      <rect x="14" y="3" width="7" height="5" rx="1.2" />
      <rect x="14" y="12" width="7" height="9" rx="1.2" />
      <rect x="3" y="16" width="7" height="5" rx="1.2" />
    </svg>
  );
}

function PipelineIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
      <rect x="3" y="4" width="5" height="16" rx="1.2" />
      <rect x="9.5" y="4" width="5" height="11" rx="1.2" />
      <rect x="16" y="4" width="5" height="7" rx="1.2" />
    </svg>
  );
}

function CompaniesIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
      <path d="M3 21h18" />
      <path d="M5 21V6a1 1 0 0 1 1-1h6a1 1 0 0 1 1 1v15" />
      <path d="M13 21V10a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v11" />
      <path d="M8 8h1M8 11h1M8 14h1" />
    </svg>
  );
}

function BillingIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
      <path d="M6 2h9l4 4v14a1 1 0 0 1-1 1H6a1 1 0 0 1-1-1V3a1 1 0 0 1 1-1z" />
      <path d="M14 2v4h4" />
      <path d="M9 12h6M9 16h6M9 8h2" />
    </svg>
  );
}

function ResetIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
      <path d="M3 12a9 9 0 1 0 3-6.7L3 8" />
      <path d="M3 3v5h5" />
    </svg>
  );
}

function LogoMark() {
  return (
    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="#fff" stroke-width="2.4">
      <circle cx="12" cy="12" r="8" opacity="0.9" />
      <circle cx="12" cy="12" r="2.4" fill="#fff" stroke="none" />
    </svg>
  );
}

/**
 * Ancore obișnuite, fără `preventDefault`: browserul schimbă hash-ul, iar
 * `usePageFromHash` din main.tsx aduce ecranul la zi. Un click și o navigare
 * programatică ajung astfel pe același drum.
 */
const NAV: { page: Page; label: string; Icon: () => JSX.Element }[] = [
  { page: "dashboard", label: "Overview", Icon: DashboardIcon },
  { page: "pipeline", label: "Sales Pipeline", Icon: PipelineIcon },
  { page: "companies", label: "Companies", Icon: CompaniesIcon },
  { page: "billing", label: "Billing", Icon: BillingIcon },
];

export function Sidebar({ page }: { page: Page }) {
  const [confirmingReset, setConfirmingReset] = useState(false);

  return (
    <nav class="sidebar" aria-label="Main navigation">
      <div class="sidebar__brand">
        <span class="sidebar__logo-mark">
          <LogoMark />
        </span>
        <span class="sidebar__brand-text">
          <span class="sidebar__brand-name">Orbit CRM</span>
          <span class="sidebar__brand-sub">Sales workspace</span>
        </span>
      </div>

      <div class="sidebar__section-label">Workspace</div>
      {NAV.map(({ page: target, label, Icon }) => (
        <a
          key={target}
          class={`sidebar__link ${page === target ? "sidebar__link--active" : ""}`}
          href={hashFor(target)}
          aria-current={page === target ? "page" : undefined}
          data-ai-id={`navigate-${target}`}
        >
          <Icon />
          {label}
        </a>
      ))}

      <div class="sidebar__spacer" />

      {/* Pagina care explică demoul: scoasă din ochi, nu de pe sit. Un item de
          meniu numit „About this demo" e primul lucru care strică iluzia de
          aplicație reală, iar Orbit e util ca demo exact cât se poartă ca un
          SaaS de client.

          Ancora rămâne în DOM fiindcă scanarea colectează `a[href]` fără nicio
          filtrare de vizibilitate (`sdk/src/builder/scan.ts` → collectLinks),
          deci ecranul intră în configul asistentului ca oricare altul. Hash-ul
          `#/about` supraviețuiește normalizării (`normalizeLink` păstrează
          hash-urile de rută SPA), deci nu se confundă cu dashboard-ul.

          `hidden`, nu o clasă de tip sr-only: aceea ar ascunde-o de ochi dar ar
          citi-o cu voce tare la screen reader — adică tot un om. */}
      <a href={hashFor("about")} data-ai-id="navigate-about" hidden>
        About this demo
      </a>

      <button type="button" class="sidebar__reset" data-ai-id="reset-demo-data" onClick={() => setConfirmingReset(true)}>
        <ResetIcon />
        Reset demo data
      </button>

      <div class="sidebar__user">
        <Avatar name="Radu Coman" size="md" />
        <span class="sidebar__user-info">
          <span class="sidebar__user-name">Radu Coman</span>
          <span class="sidebar__user-role">Sales Admin</span>
        </span>
      </div>

      {/* Singurul loc din aplicație unde un om află că totul e inventat. Era pe
          ecranul „About this demo", care nu mai e în sidebar — și o aplicație
          care își joacă rolul până la capăt, fără să spună nicăieri că e
          ficțiune, e exact ce nu vrem să publicăm. */}
      <p class="sidebar__disclosure">
        Orbit CRM is a fictional product. Every company, deal and person in it was invented to demonstrate the
        Ask My Website AI assistant.
      </p>

      {confirmingReset && (
        <ConfirmDialog
          title="Reset demo data"
          message="This will discard every change you made and restore the original demo dataset. This cannot be undone."
          confirmLabel="Reset"
          danger
          onCancel={() => setConfirmingReset(false)}
          onConfirm={() => {
            store.reset();
            setConfirmingReset(false);
            toast.success("Demo data has been reset.");
          }}
        />
      )}
    </nav>
  );
}
